import { Box } from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { styled } from "@mui/system";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "../../../redux/store";
import BookLocationNavigateBackAction from "../common/util/BookLocationNavigateBackAction";
import TenantLogo from "../../layout/components/TenantLogo";
import Header from "../../layout/components/Header";
import CloseIcon from "../../../components/icons/CloseIcon";
import BookLocationHeader from "../common/util/BookLocationHeader";
import GeneralDialog from "../../common/GeneralDialog";
import PaymentStep from "./PaymentStep";
import BookLocationNavigateNextAction from "../common/util/BookLocationNavigateNextAction";
import BookingSummary from "./BookingSummary";
import OrderData from "./OrderData";
import routes from "../../../routes/routes";
import {
  bookingCreationCompleted,
  bookingFlowChanged,
  setNextEnabled,
  setUseCase,
} from "../../../redux/slice/bookingCreation/bookingCreationSlice";

export enum BookingCreationStep {
  orderData,
  bookingSummary,
  payment,
}

const Root = styled(Box)(({ theme }) => ({
  padding: `${theme.spacing(1)}px ${theme.spacing(1.5)}px`,
  color: "white",
  height: "100%",
  boxSizing: "border-box",
  backgroundColor: theme.palette.background.default,
  paddingBottom: `calc(${theme.spacing(1)}px + env(safe-area-inset-bottom, 0))`,
  display: "flex",
  flexDirection: "column",
}));

const StepWrapper = styled(Box)(({ theme }) => ({
  flex: 1,
  width: "100%",
  maxWidth: 1000,
  margin: "0 auto",
  overflowY: "auto",
  paddingTop: theme.spacing(3),
  [theme.breakpoints.up("lg")]: {
    paddingTop: theme.spacing(6),
  },
}));

const ActionsWrapper = styled(Box)(({ theme }) => ({
  width: "calc(100% - 42px)",
  maxWidth: 1000,
  margin: "0 auto",
  paddingBottom: theme.spacing(5),
  // position: 'fixed',
  bottom: 10,
  left: 0,
  right: 0,
  [theme.breakpoints.up("lg")]: {
    paddingBottom: theme.spacing(5),
  },
}));

function GiftVoucherPage() {
  const stepWrapperRef = useRef<HTMLElement>();

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    step,
    personalDataValidation,
    voucherSelectedAmount,
    voucherCustomAmountIsValid,
  } = useSelector((state: RootState) => state.bookingCreation);
  const [pageDialogOpen, setPageDialogOpen] = useState<boolean>(false);
  const isLastStep = step === BookingCreationStep.payment;

  //   const isLoading = () => {
  //     if (
  //       step === BookingCreationStep.personalData ||
  //       step === BookingCreationStep.confirmation
  //     ) {
  //       return false;
  //     }

  //     return false;
  //   };

  const closePage = () => {
    navigate(routes.home);
    dispatch(bookingCreationCompleted());
  };

  const headerText = useMemo(() => {
    switch (step) {
      case BookingCreationStep.orderData:
        return t("book_location.buy_gift_card");
      case BookingCreationStep.bookingSummary:
        return t("book_location.git_card_summary");
      default:
        return "";
    }
  }, [step]);

  useEffect(() => {
    // dispatch(loadBookingFlow({}));
    stepWrapperRef.current = document.getElementById(
      "voucherBookingProcessStepWrapper"
    )!;
    // @todo body scroll lock breaks the phone number input. Maybe remove it entirely?
    //disableBodyScroll(stepWrapperRef.current);
    //return () => enableBodyScroll(stepWrapperRef.current!);
    if (step === BookingCreationStep.orderData) {
      // Set the usecase
      dispatch(setUseCase({ useCase: "voucher" }));

      // Set the bookingFlow
      dispatch(
        bookingFlowChanged({
          bookingFlow: ["orderData", "bookingSummary", "payment"],
        })
      );
    }
  }, []);

  useEffect(() => {
    if (
      personalDataValidation.email &&
      personalDataValidation.firstName &&
      personalDataValidation.lastName &&
      personalDataValidation.isAgreed
    ) {
      if (
        voucherSelectedAmount === t("general.voucher_purchase.custom_amount")
      ) {
        if (voucherCustomAmountIsValid) {
          dispatch(setNextEnabled(true));
        } else {
          dispatch(setNextEnabled(false));
        }
      } else if (voucherSelectedAmount) {
        dispatch(setNextEnabled(true));
      } else {
        dispatch(setNextEnabled(false));
      }
    }
  }, [
    personalDataValidation,
    voucherCustomAmountIsValid,
    dispatch,
    voucherSelectedAmount,
    t,
  ]);

  useEffect(() => {
    const manageReduxData = async () => {
      if (step === BookingCreationStep.bookingSummary) {
        dispatch(setNextEnabled(true));
      }
    };
    manageReduxData();
  }, [step]);

  return (
    <Root>
      <Header>
        <div className="h-12 absolute left-2 flex items-center">
          <BookLocationNavigateBackAction />
        </div>
        <TenantLogo />
        <button
          className="size-12 absolute right-0 text-foreground/90"
          onClick={() => setPageDialogOpen(true)}
        >
          <CloseIcon />
        </button>
      </Header>
      <Box sx={{ marginTop: "20px" }}>
        <BookLocationHeader text={headerText} />
      </Box>
      <StepWrapper id={"bookingProcessStepWrapper"}>
        {step === BookingCreationStep.orderData && <OrderData />}
        {step === BookingCreationStep.bookingSummary && <BookingSummary />}

        {step === BookingCreationStep.payment && <PaymentStep />}
      </StepWrapper>
      <ActionsWrapper>
        {!isLastStep && <BookLocationNavigateNextAction />}
      </ActionsWrapper>
      {/* <LoaderOverlay open={isLoading()} /> */}
      <GeneralDialog
        open={pageDialogOpen}
        onClose={() => setPageDialogOpen(false)}
        onContinue={closePage}
        content={t("use_order.closing_dialog.content")}
      />
    </Root>
  );
}

export default GiftVoucherPage;
